import {
  SecurityFindingImpactLevel,
  SecurityFindingKind,
  SecurityFindingSeverityLevel,
  type Permission,
} from "$lib/models/base";
import type { SecurityFinding } from "$lib/stores/orgSecurityFindings";
import { filter, groupBy, map, max } from "lodash";
import { Colors } from "../ui/badge";

/**
 * Defines the severity thresholds and their corresponding SecurityFindingSeverity levels
 * Ordered from highest to lowest severity
 */
export const SEVERITY_RANGES = [
  { min: 0.8, max: 1.1, level: SecurityFindingSeverityLevel.CRITICAL }, // to capture the 1.0 severity as critical
  { min: 0.6, max: 0.8, level: SecurityFindingSeverityLevel.HIGH },
  { min: 0.4, max: 0.6, level: SecurityFindingSeverityLevel.MEDIUM },
  { min: 0.0, max: 0.4, level: SecurityFindingSeverityLevel.LOW },
] as const;

export const SEVERITY_COLORS = {
  [SecurityFindingSeverityLevel.CRITICAL]: Colors.RED,
  [SecurityFindingSeverityLevel.HIGH]: Colors.ORANGE,
  [SecurityFindingSeverityLevel.MEDIUM]: Colors.YELLOW,
  [SecurityFindingSeverityLevel.LOW]: Colors.BLUE,
} as const;

/**
 * Maps a severity score to a SecurityFindingSeverity level
 * @param severityScore - Severity score between 0 and 1
 * @returns SecurityFindingSeverity enum value
 */
export const mapSeverityScoreToLevel = (severityScore: number | undefined): SecurityFindingSeverityLevel => {
  if (severityScore === undefined) {
    return SecurityFindingSeverityLevel.LOW;
  }

  const range = SEVERITY_RANGES.find((range) => severityScore >= range.min && severityScore < range.max);
  return range?.level ?? SecurityFindingSeverityLevel.LOW;
};

export const mapSeverityColour = (severityLevel: SecurityFindingSeverityLevel): Colors => {
  return SEVERITY_COLORS[severityLevel] ?? SEVERITY_COLORS[SecurityFindingSeverityLevel.LOW];
};

/**
 * Defines the impact thresholds and their corresponding SecurityFindingImpact levels
 * Ordered from highest to lowest severity
 */
export const IMPACT_RANGES = [
  { min: 0.8, max: 1.1, level: SecurityFindingImpactLevel.CRITICAL }, // to capture the 1.0 severity as critical
  { min: 0.6, max: 0.8, level: SecurityFindingImpactLevel.HIGH },
  { min: 0.4, max: 0.6, level: SecurityFindingImpactLevel.MEDIUM },
  { min: 0.0, max: 0.4, level: SecurityFindingImpactLevel.LOW },
] as const;

export const IMPACT_COLORS = {
  [SecurityFindingImpactLevel.CRITICAL]: Colors.RED,
  [SecurityFindingImpactLevel.HIGH]: Colors.ORANGE,
  [SecurityFindingImpactLevel.MEDIUM]: Colors.YELLOW,
  [SecurityFindingImpactLevel.LOW]: Colors.BLUE,
} as const;

export const mapImpactScoreToLevel = (impactScore: number | undefined): SecurityFindingImpactLevel => {
  if (impactScore === undefined) {
    return SecurityFindingImpactLevel.LOW;
  }

  const range = IMPACT_RANGES.find((range) => impactScore >= range.min && impactScore < range.max);

  return range?.level ?? SecurityFindingImpactLevel.LOW;
};

export const mapImpactColour = (impactLevel: SecurityFindingImpactLevel): Colors => {
  return IMPACT_COLORS[impactLevel] ?? IMPACT_COLORS[SecurityFindingImpactLevel.LOW];
};

const computeImpactScoreForAnomalousPermissionsOnSensitiveDocument = (
  securityFindings: SecurityFinding[],
): SecurityFinding[] => {
  // For anomalous permissions on sensitive document, the impact is the number of impacted principals
  // multiplied by the severity
  let result: SecurityFinding[] = [];
  const maxNumberOfImpactedPrincipals = max(securityFindings.map((finding) => finding.numberOfImpactedPrincipals)) || 0;
  for (let finding of securityFindings) {
    const impact =
      ((finding.numberOfImpactedPrincipals || 0) / (maxNumberOfImpactedPrincipals || 1)) * finding.severity;
    const impactLevel = mapImpactScoreToLevel(impact);
    result.push({ ...finding, impact, impactLevel });
  }

  return result;
};

const computeImpactScoreForPublicDocument = (findings: SecurityFinding[]): SecurityFinding[] => {
  // TODO: This is temporary, we should update after understanding the findings
  // For public document, the impact is the severity
  return findings.map((finding) => ({
    ...finding,
    impact: finding.severity,
    impactLevel: mapImpactScoreToLevel(finding.severity),
  }));
};

const computeImpactScoreForDocumentAccessedByExternalPrincipal = (findings: SecurityFinding[]): SecurityFinding[] => {
  // For document accessed by external principal, the impact is the severity multiplied by the number of external principals
  // divided by the maximum acceptable number of external principals

  // TODO: Tune this value
  const maxAcceptableNumberOfExternalPrincipals = 0;
  return findings.map((finding) => ({
    ...finding,
    impact:
      filter(finding?.permissions, (permission) => permission.principalIsExternal).length >
      maxAcceptableNumberOfExternalPrincipals
        ? finding.severity
        : 0,
    impactLevel: mapImpactScoreToLevel(
      filter(finding?.permissions, (permission) => permission.principalIsExternal).length >
        maxAcceptableNumberOfExternalPrincipals
        ? finding.severity
        : 0,
    ),
  }));
};

const computeImpactScoreForLargeScaleCollaborationOnPersonalDrive = (
  findings: SecurityFinding[],
): SecurityFinding[] => {
  // TODO: This is temporary, we should update after understanding the findings
  // For large scale collaboration on personal drive, the impact is the number of documents in the personal drive
  const maxNumberOfDocumentsInPersonalDrive =
    max(findings.map((finding) => finding.numberOfDocumentsInPersonalDrive)) || 0;
  return findings.map((finding) => ({
    ...finding,
    impact: (finding.numberOfDocumentsInPersonalDrive || 1) / maxNumberOfDocumentsInPersonalDrive,
    impactLevel: mapImpactScoreToLevel(
      (finding.numberOfDocumentsInPersonalDrive || 1) / maxNumberOfDocumentsInPersonalDrive,
    ),
  }));
};

const computeImpactScoreForPermissionsManagedThroughSecondaryIdentity = (
  findings: SecurityFinding[],
): SecurityFinding[] => {
  // TODO: This is temporary, we should update after understanding the findings
  // For permissions managed through site users and site groups, the impact is the number of permissions
  const maxNumberOfPermissions = 20;
  return findings.map((finding) => ({
    ...finding,
    impact: (finding.totalNumberOfPermissions || 1) / maxNumberOfPermissions,
    impactLevel: mapImpactScoreToLevel((finding.totalNumberOfPermissions || 1) / maxNumberOfPermissions),
  }));
};

const computeImpactScoreForPersonalDriveWithSensitiveDocuments = (findings: SecurityFinding[]): SecurityFinding[] => {
  // TODO: This is temporary, we should update after understanding the findings
  // For personal drive with sensitive documents, the impact is the number of permissions

  return findings.map((finding) => ({
    ...finding,
    impact: (finding.totalNumberOfSensitiveDocuments || 0) / (finding.totalNumberOfDocuments || 1),
    impactLevel: mapImpactScoreToLevel(
      (finding.totalNumberOfSensitiveDocuments || 0) / (finding.totalNumberOfDocuments || 1),
    ),
  }));
};

export const computeImpactScorePerKind = (securityFindings: SecurityFinding[]): SecurityFinding[] => {
  if (securityFindings.length === 0) {
    return securityFindings;
  }

  const securityFindingsByKind = groupBy(securityFindings, "securityFindingKind");
  let result: SecurityFinding[] = [];
  for (const kind in securityFindingsByKind) {
    const findings = securityFindingsByKind[kind];
    if (kind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT) {
      const findingWithImpactStore = computeImpactScoreForAnomalousPermissionsOnSensitiveDocument(findings);
      result = [...result, ...findingWithImpactStore];
    } else if (kind == SecurityFindingKind.PUBLIC_DOCUMENT) {
      const findingWithImpactStore = computeImpactScoreForPublicDocument(findings);
      result = [...result, ...findingWithImpactStore];
    } else if (kind == SecurityFindingKind.LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE) {
      const findingWithImpactStore = computeImpactScoreForLargeScaleCollaborationOnPersonalDrive(findings);
      result = [...result, ...findingWithImpactStore];
    } else if (kind == SecurityFindingKind.PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY) {
      const findingWithImpactStore = computeImpactScoreForPermissionsManagedThroughSecondaryIdentity(findings);
      result = [...result, ...findingWithImpactStore];
    } else if (kind == SecurityFindingKind.PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS) {
      const findingWithImpactStore = computeImpactScoreForPersonalDriveWithSensitiveDocuments(findings);
      result = [...result, ...findingWithImpactStore];
    } else if (kind == SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL) {
      const findingWithImpactStore = computeImpactScoreForDocumentAccessedByExternalPrincipal(findings);
      result = [...result, ...findingWithImpactStore];
    } else {
      result = [...result, ...findings];
    }
  }
  return result;
};

/**
 * Shape a `Document Accessed by External Principal` finding for display.
 *
 * A `permissions` entry is a grant, not a person: one principal routinely holds several on a
 * document. De-duplicated on email rather than resource id because one human legitimately has two
 * principal ids (an Entra `user` and a SharePoint `siteuser`), and the list on screen shows emails.
 */
export function summariseExternalPrincipals(rawPermissions: Permission[] | undefined) {
  // Externality is a tri-state: unclassified is not the same as internal, so only true/false are kept.
  const classified = filter(rawPermissions || [], (permission: Permission) =>
    ["true", "false"].includes(
      (permission?.principalIsExternal !== undefined ? permission?.principalIsExternal.toString() : "").toLowerCase(),
    ),
  );

  const permissions = map(classified, (permission: Permission) => ({
    ...permission,
    principalIsExternal: permission?.principalIsExternal?.toString().toLowerCase() === "true",
  }));

  const external = filter(permissions, (permission) => permission.principalIsExternal);

  const seen = new Set<string>();
  const distinct = external.filter((permission) => {
    const identity = permission.principalEmail || permission.principalResourceId;
    // Unidentifiable rows are kept: under-reporting exposure is the worse failure here.
    if (!identity) return true;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });

  return {
    permissions,
    numberOfExternalPrincipals: distinct.length,
    recommendations: distinct.map((p) => ({
      email: p.principalEmail,
      recommendedAction: (p as any).recommendedAction,
    })),
  };
}
